// Membership panel — where the club's membership actually stands.
//
// Rendered as a tab by the Reports page; it was its own page until the admin
// tiles were consolidated. Loads its own data, which is why it can sit beside
// panels that know nothing about memberships.
//
// Answers, in order: how many accounts hold a membership at all, how many of
// those are current, what that is worth, and which tiers people are on.
//
// On the charts: this is modelled on a report that drew everything as pie
// charts. Pies are not used here. A two-slice pie is a ratio, which reads
// better as a meter; a five-slice pie is part-to-whole, which reads better as
// a stacked bar; and a pie of eight tiers is a table wearing a costume. Every
// figure below is also present as a number in a table, so the charts are the
// summary and never the only way to read the data.
//
// On colour: the bands are the validated categorical slots rather than
// red/green "good/bad" — red beside green is ΔE 4.1 under deuteranopia, which
// is the single most common way a status chart becomes unreadable. Meaning is
// carried by the labels; the colour only ties a segment to its row.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import {
  Box, Typography, Paper, Grid, CircularProgress, Alert, Checkbox,
  FormControlLabel, Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
} from '@mui/material';
import { membershipStatus } from '../../utils/membership';
import { logError } from '../../utils/errorLogger';

const INK = '#1E3A5F';
const TEXT_2 = '#52514e';
const MUTED = '#8a8880';
const SURFACE = '#fcfcfb';

// Validated categorical slots 1–3 (light). Adjacent CVD ΔE 9.2, normal-vision
// 27.6 — both clear. Aqua is below 3:1 on this surface, so every band carries a
// visible label, which is the documented relief.
const BAND = { current: '#2a78d6', attention: '#eb6834', inactive: '#1baf7a' };

// Tier bars are one hue. Bar LENGTH already encodes the count, so stepping the
// colour as well would imply a second variable that does not exist — and with
// eight tiers against a four-step ramp, the bottom five would share a step and
// look like a category anyway.
const BAR = '#2a78d6';

// Which effective statuses fall in which band. 'expired' is derived by
// membershipStatus() for a lapsed day pass, which is why it is not a stored
// value anywhere.
const BANDS = [
  { key: 'current', label: 'Current', color: BAND.current,
    statuses: ['active', 'trialing'], note: 'Full access — paying or prepaid' },
  { key: 'attention', label: 'Needs attention', color: BAND.attention,
    statuses: ['past_due'], note: 'Payment retrying' },
  { key: 'inactive', label: 'Not current', color: BAND.inactive,
    statuses: ['frozen', 'expired', 'canceled', 'cancelled'], note: 'Paused, lapsed or cancelled' },
];

const STATUS_LABEL = {
  active: 'Active', trialing: 'Trialing (prepaid)', past_due: 'Past due',
  frozen: 'Frozen', expired: 'Expired', canceled: 'Canceled', cancelled: 'Canceled',
};

const money = (n) => `$${Math.round(n).toLocaleString('en-US')}`;
const pct = (n, d) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '0.0%');

/** A ratio against a total. Replaces the two-slice pie — same fact, less ink. */
function Meter({ value, total, color, label }) {
  const share = total > 0 ? value / total : 0;
  return (
    <Box>
      <Box sx={{ position: 'relative', height: 10, borderRadius: 5, backgroundColor: '#e8e8e4', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, width: `${share * 100}%`, backgroundColor: color, borderRadius: 5 }} />
      </Box>
      <Typography variant="caption" sx={{ color: TEXT_2, display: 'block', mt: 0.75 }}>
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Part-to-whole as one horizontal bar.
 *
 * 2px of surface between segments so touching fills stay separable, and 4px
 * rounded ends on the outermost segments only — the bar reads as one object
 * with cuts in it rather than as loose blocks.
 */
function StackedBar({ parts, total }) {
  if (!total) return null;
  const shown = parts.filter(p => p.value > 0);
  return (
    <Box sx={{ display: 'flex', height: 28, gap: '2px', mb: 1.5 }}>
      {shown.map((p, i) => (
        <Tooltip key={p.key} title={`${p.label}: ${p.value} (${pct(p.value, total)})`}>
          <Box sx={{
            flexGrow: p.value, minWidth: 3, backgroundColor: p.color, cursor: 'default',
            borderTopLeftRadius: i === 0 ? 4 : 0, borderBottomLeftRadius: i === 0 ? 4 : 0,
            borderTopRightRadius: i === shown.length - 1 ? 4 : 0,
            borderBottomRightRadius: i === shown.length - 1 ? 4 : 0,
          }} />
        </Tooltip>
      ))}
    </Box>
  );
}

/** Magnitude, low→high. A sorted bar beats a pie the moment slices get close. */
function TierBars({ rows, max }) {
  return (
    <Box>
      {rows.map((r) => (
        <Box key={r.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.6 }}>
          <Typography variant="body2" sx={{ width: 190, flexShrink: 0, color: INK }}>{r.name}</Typography>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={`${r.name}: ${r.count}`}>
              <Box sx={{
                height: 14, borderRadius: '0 4px 4px 0', minWidth: r.count ? 3 : 0,
                width: `${max > 0 ? (r.count / max) * 100 : 0}%`,
                backgroundColor: BAR,
              }} />
            </Tooltip>
            <Typography variant="caption" sx={{ color: TEXT_2, fontWeight: 600 }}>{r.count}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function Section({ title, children, sx }) {
  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e8e8e4', backgroundColor: SURFACE, height: '100%', ...sx }}>
      {title && (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: INK, mb: 1.5 }}>{title}</Typography>
      )}
      {children}
    </Paper>
  );
}

export default function MembershipPanel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [excludeLapsed, setExcludeLapsed] = useState(false);
  // Revenue comes from Stripe, not from the mirror of it on the user docs.
  // null while loading; false if the call fails, so the panel can say so
  // instead of quietly falling back to a number that is known to be low.
  const [revenue, setRevenue] = useState(null);
  const [asOf] = useState(() => new Date());

  useEffect(() => {
    (async () => {
      try {
        const [u, t] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'membershipTiers')),
        ]);
        // Alias stubs point at another account; counting them would double-count
        // the same person's membership.
        setUsers(u.docs.map(d => ({ id: d.id, ...d.data() })).filter(x => !x.mergedIntoUid));
        setTiers(t.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        logError('MembershipPanel', e.message, e.stack);
        setError('Could not load the report: ' + e.message);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const fn = httpsCallable(getFunctions(), 'membershipRevenue', { timeout: 300000 });
        const { data } = await fn({});
        setRevenue(data);
      } catch (e) {
        logError('MembershipPanel.revenue', e.message, e.stack);
        setRevenue(false);
      }
    })();
  }, []);

  const data = useMemo(() => {
    const tierById = new Map(tiers.map(t => [t.id, t]));
    const tierByName = new Map(tiers.map(t => [t.name, t]));

    // Effective status, not the stored one: a day pass is written 'active' and
    // nothing ever sweeps it, so expiry is derived at read time.
    const held = users
      .filter(u => u.membership)
      .map(u => {
        const m = u.membership;
        const tier = tierById.get(m.tierId) || tierByName.get(m.tierName) || null;
        return { id: u.id, status: membershipStatus(u) || 'none', tier, m };
      });

    const bandOf = (s) => BANDS.find(b => b.statuses.includes(s))?.key || 'inactive';
    const visible = excludeLapsed ? held.filter(h => bandOf(h.status) === 'current') : held;

    const byStatus = {};
    held.forEach(h => { byStatus[h.status] = (byStatus[h.status] || 0) + 1; });

    const bandCounts = BANDS.map(b => ({
      ...b, value: held.filter(h => bandOf(h.status) === b.key).length,
    }));

    // No revenue is computed here. It used to be, from tier prices on the user
    // docs, and it reported roughly half the truth because a subscription only
    // reaches those docs once its payer has a Torch account. Stripe answers
    // that question now, in the panel above; a second local calculation would
    // only be a wrong number waiting to be trusted.

    const tierRows = Object.entries(
      visible.reduce((acc, h) => {
        const n = h.tier?.name || h.m.tierName || 'No tier recorded';
        acc[n] = (acc[n] || 0) + 1;
        return acc;
      }, {}),
    ).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);

    // Who the accounts without a membership are. The bare count invites the
    // wrong conclusion — that 148 people failed to buy something — when most of
    // them are parents and staff for whom a membership was never the point.
    const heldIds = new Set(held.map(h => h.id));
    const without = users.filter(u => !heldIds.has(u.id));
    const roleBucket = (u) => {
      const r = u.roles || [];
      if (r.includes('coach') || r.includes('assistant_coach') || r.includes('contractor')) return 'Coaches & staff';
      if (u.isParent === true || r.includes('parent')) return 'Parents';
      if (r.some(x => ['adult_athlete', 'player', 'member'].includes(x))) return 'Players, no membership';
      return 'Other accounts';
    };
    const withoutBy = Object.entries(
      without.reduce((a, u) => ({ ...a, [roleBucket(u)]: (a[roleBucket(u)] || 0) + 1 }), {}),
    ).sort((a, b) => b[1] - a[1]);

    return {
      totalAccounts: users.length,
      withoutBy,
      held: held.length,
      byStatus, bandCounts, tierRows,
      visibleCount: visible.length,
    };
  }, [users, tiers, excludeLapsed]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  const maxTier = Math.max(1, ...data.tierRows.map(r => r.count));
  const statusRows = Object.entries(data.byStatus).sort((a, b) => b[1] - a[1]);

  return (
    <Box sx={{ maxWidth: 1100 }}>
        <Typography variant="body2" sx={{ color: TEXT_2, mb: 3 }}>
          Where memberships stand right now. Statuses are derived at read time, so a lapsed
          day pass counts as expired rather than as the "active" it was last written as.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2.5}>
          {/* ── Accounts ─────────────────────────────────────────────── */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Section>
              <Typography sx={{ fontSize: '2.75rem', lineHeight: 1, fontWeight: 700, color: INK }}>
                {data.totalAccounts}
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_2, mb: 2 }}>
                accounts · {data.held} hold a membership
              </Typography>
              <Meter value={data.held} total={data.totalAccounts} color={BAND.current}
                label={`${data.held} with a membership (${pct(data.held, data.totalAccounts)}) · ${data.totalAccounts - data.held} without`} />
              <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1.5, mb: 0.5 }}>
                An account is not a membership. Most of the rest were never meant to hold one:
              </Typography>
              <Table size="small">
                <TableBody>
                  {data.withoutBy.map(([label, n]) => (
                    <TableRow key={label}>
                      <TableCell sx={{ border: 0, pl: 0, py: 0.4, color: TEXT_2 }}>{label}</TableCell>
                      <TableCell align="right" sx={{ border: 0, pr: 0, py: 0.4, fontWeight: 600 }}>{n}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          </Grid>

          {/* ── Revenue ──────────────────────────────────────────────── */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Section>
              {revenue === null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" sx={{ color: TEXT_2 }}>Reading Stripe…</Typography>
                </Box>
              )}
              {revenue === false && (
                <Alert severity="warning" sx={{ mb: 0 }}>
                  Couldn't reach Stripe, so revenue is not shown. The membership counts on
                  this page come from Torch accounts and are unaffected.
                </Alert>
              )}
              {revenue && (
                <>
                  <Typography sx={{ fontSize: '2.75rem', lineHeight: 1, fontWeight: 700, color: INK }}>
                    {money(revenue.total.arr)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: TEXT_2, mb: 2 }}>
                    annual recurring revenue · {revenue.total.subs} live subscriptions in Stripe
                  </Typography>
                  {/* By income source first — junior training and adult
                      membership are both recurring and are different
                      businesses; one ARR figure hides which is growing. */}
                  <Table size="small">
                    <TableBody>
                      {(revenue.byCategory || []).map(c => (
                        <TableRow key={c.key}>
                          <TableCell sx={{ border: 0, pl: 0, color: TEXT_2 }}>
                            {c.label}
                            <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>
                              {c.subs} subscription{c.subs === 1 ? '' : 's'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 0, pr: 0, fontWeight: 600, verticalAlign: 'top' }}>
                            {money(c.arr)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 2, mb: 0.5, fontWeight: 600 }}>
                    How much of that this page can see
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ border: 0, pl: 0, color: TEXT_2 }}>
                          Linked to a Torch account
                          <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>
                            {revenue.linked.subs} subscriptions — what the counts below can see
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ border: 0, pr: 0, fontWeight: 600, verticalAlign: 'top' }}>
                          {money(revenue.linked.arr)}
                        </TableCell>
                      </TableRow>
                      {revenue.unlinked.subs > 0 && (
                        <TableRow>
                          <TableCell sx={{ border: 0, pl: 0, color: TEXT_2 }}>
                            <Box component="span" sx={{ color: BAND.attention, fontWeight: 600 }}>Not linked</Box>
                            <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>
                              {revenue.unlinked.subs} paying, invisible to this page —{' '}
                              {revenue.unlinked.withoutAccount} with no Torch account,{' '}
                              {revenue.unlinked.withAccount} with an account but no membership recorded
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 0, pr: 0, fontWeight: 600, verticalAlign: 'top' }}>
                            {money(revenue.unlinked.arr)}
                          </TableCell>
                        </TableRow>
                      )}
                      {revenue.pastDue.subs > 0 && (
                        <TableRow>
                          <TableCell sx={{ border: 0, pl: 0, color: TEXT_2 }}>
                            Past due, not counted
                            <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>
                              {revenue.pastDue.subs} retrying · {money(revenue.pastDue.arr)}/yr if they recover
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 0, pr: 0, color: MUTED, verticalAlign: 'top' }}>—</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1.5 }}>
                    Straight from Stripe, annualised per plan — $250 every 12 months and $25
                    every month both normalise to a year. Everything below this panel is
                    counted from Torch accounts, so it describes the linked half only.
                  </Typography>
                </>
              )}
            </Section>
          </Grid>

          {/* ── Standing ─────────────────────────────────────────────── */}
          <Grid size={{ xs: 12 }}>
            <Section title={`Standing — ${data.held} memberships`}>
              <StackedBar parts={data.bandCounts} total={data.held} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Table size="small">
                    <TableBody>
                      {data.bandCounts.map(b => (
                        <TableRow key={b.key}>
                          <TableCell sx={{ border: 0, pl: 0, width: 18 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: b.color }} />
                          </TableCell>
                          <TableCell sx={{ border: 0, pl: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.label}</Typography>
                            <Typography variant="caption" sx={{ color: MUTED }}>{b.note}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 0, fontWeight: 600 }}>{b.value}</TableCell>
                          <TableCell align="right" sx={{ border: 0, pr: 0, color: TEXT_2, width: 64 }}>
                            {pct(b.value, data.held)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ pl: 0, color: MUTED }}>Exact status</TableCell>
                        <TableCell align="right" sx={{ pr: 0, color: MUTED }}>Members</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statusRows.map(([s, n]) => (
                        <TableRow key={s}>
                          <TableCell sx={{ border: 0, pl: 0 }}>{STATUS_LABEL[s] || s}</TableCell>
                          <TableCell align="right" sx={{ border: 0, pr: 0, fontWeight: 600 }}>{n}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
            </Section>
          </Grid>

          {/* ── Tiers ────────────────────────────────────────────────── */}
          <Grid size={{ xs: 12 }}>
            <Section title={`Tiers — ${data.visibleCount} memberships`}>
              <FormControlLabel
                sx={{ mb: 1.5 }}
                control={<Checkbox size="small" checked={excludeLapsed}
                  onChange={e => setExcludeLapsed(e.target.checked)} />}
                label={<Typography variant="body2">Current members only</Typography>} />
              {data.tierRows.length === 0 ? (
                <Typography variant="body2" sx={{ color: MUTED }}>Nothing to show.</Typography>
              ) : (
                <TierBars rows={data.tierRows} max={maxTier} />
              )}
            </Section>
          </Grid>
        </Grid>

        <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 2.5 }}>
          As of {asOf.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
        </Typography>
    </Box>
  );
}
